import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField } from '../components/Field.jsx';
import { InfoAlert } from '../components/States.jsx';
import useForm from '../hooks/useForm.js';
import { useAuth } from '../store/useAuth.js';
import { useChangePasswordMutation } from '../store/apiSlice.js';
import { changePasswordSchema, MIN_PASSWORD } from '../validation/schemas.js';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '../validation/roles.js';
import { useToast } from '../context/ToastContext.jsx';
import { dash } from '../utils/format.js';

const BLANK = { currentPassword: '', newPassword: '', confirmPassword: '' };

export default function AccountPage() {
  const { user } = useAuth();
  const showToast = useToast();
  const form = useForm(BLANK, changePasswordSchema);
  const [changePassword, { isLoading: busy }] = useChangePasswordMutation();

  const submit = async (event) => {
    event.preventDefault();
    if (!form.validate()) return;

    try {
      const { confirmPassword, ...payload } = form.values;
      await changePassword(payload).unwrap();
      form.reset(BLANK);
      showToast('Password changed. Other devices have been signed out.');
    } catch (err) {
      form.applyServerErrors(err.details);
      showToast(err.message, 'error');
    }
  };

  return (
    <>
      <PageHeader eyebrow="Account" title="My Account" subtitle="Your details and password." />

      <Card title="Signed in as" tag={ROLE_LABELS[user.role] || user.role}>
        <div className="form-grid cols-3">
          <div className="field">
            <label>Name</label>
            <div className="readonly-value">{user.name}</div>
          </div>
          <div className="field">
            <label>Email</label>
            <div className="readonly-value">{user.email}</div>
          </div>
          <div className="field">
            <label>Phone</label>
            <div className="readonly-value">{dash(user.phone1)}</div>
          </div>
        </div>
        <InfoAlert>{ROLE_DESCRIPTIONS[user.role]}</InfoAlert>
        <div className="module-grid" style={{ marginTop: 10 }}>
          {(user.role === 'admin' ? ['Everything'] : user.modules || []).map((moduleName) => (
            <span key={moduleName} className="module-pill on static">
              {moduleName}
            </span>
          ))}
        </div>
      </Card>

      <Card title="Change Password" tag="Security">
        <form onSubmit={submit} noValidate>
          <div className="form-grid cols-3">
            <TextField type="password" label="Current Password" autoComplete="current-password" {...form.fieldProps('currentPassword')} />
            <TextField
              type="password"
              label="New Password"
              autoComplete="new-password"
              hint={`${MIN_PASSWORD}+ characters, one letter and one number.`}
              {...form.fieldProps('newPassword')}
            />
            <TextField type="password" label="Confirm New Password" autoComplete="new-password" {...form.fieldProps('confirmPassword')} />
          </div>
          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={busy}>
              {busy ? 'Changing…' : 'Change Password'}
            </button>
          </div>
          <p className="muted" style={{ fontSize: 11.5, marginBottom: 0 }}>
            Changing your password signs you out on every other device.
          </p>
        </form>
      </Card>
    </>
  );
}
